import {
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  PlusOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { Button, Col, Popconfirm, Row, Tooltip, Typography } from 'antd';
import config from 'config';
import { format } from 'date-fns';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './styles.module.scss';

const { Paragraph } = Typography;

type DonorNote = {
  id: string;
  text: string;
  createdAt: string;
};

type PropTypes = {
  notes: DonorNote[];
  onAdd: () => void;
  onEdit: (noteId: string, text: string) => void;
  onDelete: (noteId: string) => void;
};

export const DonorNotesCell: FC<PropTypes> = ({
  notes,
  onAdd,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation('donor.ReserveDonorsTable');
  const [expanded, setExpanded] = useState(false);

  if (!notes || notes.length === 0) {
    return (
      <Button size="small" icon={<PlusOutlined />} onClick={onAdd}>
        {t('addNote')}
      </Button>
    );
  }

  const displayNotes = expanded ? notes : notes.slice(0, 1);
  const hasMore = notes.length > 1;

  return (
    <div className={styles.notesCell}>
      {displayNotes.map((note) => (
        <Row key={note.id} gutter={[8, 8]} className={styles.noteRow}>
          <Col span={24}>
            <div className={styles.noteHeader}>
              <span className={styles.date}>
                {format(new Date(note.createdAt), config.DATE_FORMATS.DATE)}
              </span>
              <div className={styles.actions}>
                <Tooltip title={t('edit')}>
                  <EditOutlined onClick={() => onEdit(note.id, note.text)} />
                </Tooltip>
                <Popconfirm
                  title={t('deleteNoteConfirm')}
                  onConfirm={() => onDelete(note.id)}
                  okText={t('yes')}
                  cancelText={t('no')}
                >
                  <Tooltip title={t('delete')}>
                    <DeleteOutlined />
                  </Tooltip>
                </Popconfirm>
              </div>
            </div>
          </Col>
          <Col span={24}>
            <Paragraph
              ellipsis={{
                rows: expanded ? undefined : 2,
                expandable: false,
              }}
              className={styles.noteText}
            >
              {note.text}
            </Paragraph>
          </Col>
        </Row>
      ))}

      <Row gutter={[8, 8]} className={styles.footer}>
        {hasMore && (
          <Col>
            <Button
              type="link"
              size="small"
              icon={expanded ? <UpOutlined /> : <DownOutlined />}
              onClick={() => setExpanded(!expanded)}
            >
              {expanded
                ? t('showLess')
                : t('showMore', { count: notes.length - 1 })}
            </Button>
          </Col>
        )}
        <Col>
          <Button
            type="link"
            size="small"
            icon={<PlusOutlined />}
            onClick={onAdd}
          >
            {t('addNote')}
          </Button>
        </Col>
      </Row>
    </div>
  );
};
